import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useHistory, useLocation } from 'react-router-dom';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { Timeline } from 'primereact/timeline';
import { Tag } from 'primereact/tag';
import { Accordion, AccordionTab } from 'primereact/accordion';
import { UserService } from '../../../service/UserService';
import { SessionsCalendarHeatmap } from '../components/SessionsCalendarHeatmap';

const userService = new UserService();
const PAGE_SIZE = 10;
const SCROLL_THRESHOLD_PX = 200;

const platformIcon = (platform) => {
    const value = (platform || '').toLowerCase();
    if (value.includes('android')) return 'pi pi-android';
    if (value.includes('ios')) return 'pi pi-apple';
    return 'pi pi-mobile';
};

const ATTEMPT_TYPE_LABEL = {
    APP_LOGIN: 'Login no app',
    REGISTRATION_CODE: 'Código de cadastro',
    PASSWORD_RECOVERY_CODE: 'Código de recuperação de senha',
};

const ATTEMPT_TYPE_ICON = {
    APP_LOGIN: 'pi pi-sign-in',
    REGISTRATION_CODE: 'pi pi-key',
    PASSWORD_RECOVERY_CODE: 'pi pi-key',
};

const ATTEMPT_REASON_LABEL = {
    CODE_INVALID: 'Código incorreto',
    CODE_EXPIRED: 'Código expirado',
    USER_NOT_FOUND: 'Usuário não encontrado',
    ALREADY_VERIFIED: 'Conta já verificada',
};

const formatDateTime = (value) => {
    if (!value) return '—';
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? value : date.toLocaleString('pt-BR');
};

// durationSeconds vem sempre inteiro (AppSession.durationSeconds).
const formatDuration = (totalSeconds) => {
    if (totalSeconds == null) return '—';
    const seconds = Math.round(totalSeconds);
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    if (h > 0) return `${h}h ${String(m).padStart(2, '0')}min`;
    if (m > 0) return `${m}min ${String(s).padStart(2, '0')}s`;
    return `${s}s`;
};

const userDisplayName = (user) => {
    if (!user) return '';
    const name = [user.firstName, user.lastName].filter(Boolean).join(' ');
    return name || user.email;
};

// Mesmo padrão de StudentTimelinePage.js: página própria (não modal), pra
// poder ser acessada/recarregada direto pela URL — /users/:id/activity-timeline.
// Lista, intercalados por horário, as sessões de uso (AppSession) e as
// tentativas de login/código de verificação (LoginAttempt) do usuário, mais
// recente primeiro, com lazy loading + infinite scroll de verdade (acompanha
// o scroll da PÁGINA inteira, não uma caixa interna com altura fixa).
export const UserActivityTimelinePage = () => {
    const { id } = useParams();
    const history = useHistory();
    const location = useLocation();
    // A linha da grid de Usuários já tem nome/email — chega pelo state da
    // navegação (history.push(path, { user: rowData })), usado como valor
    // inicial sem esperar a primeira página responder. Ainda busca
    // /users/{id} em segundo plano pra funcionar também em reload direto da
    // URL, onde esse state não existe.
    const [user, setUser] = useState(location.state?.user ?? null);
    const [items, setItems] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [loading, setLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [error, setError] = useState(null);
    // Heatmap e timeline num único container (mesmo header/botão voltar da
    // página, em vez de dois cards separados) — accordion com os dois
    // abertos por padrão, só pra poder recolher o que não interessar no
    // momento.
    const [activeIndices, setActiveIndices] = useState([0, 1]);
    const [reloading, setReloading] = useState(false);
    const heatmapRef = useRef(null);
    // Ticker só pra sessão(ões) em andamento (endedAt null) — o
    // durationSeconds do backend só reflete o último sync do app (offline-
    // first, em lote), então fica parado/zerado enquanto a sessão segue
    // aberta. Recalculamos a duração como now - startedAt no front, ao vivo,
    // em vez de mostrar um valor sabidamente desatualizado.
    const [now, setNow] = useState(() => Date.now());
    const hasOpenSession = items.some(item => item.kind === 'session' && !item.endedAt);

    useEffect(() => {
        if (!hasOpenSession) return;
        const interval = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(interval);
    }, [hasOpenSession]);

    const fetchPage = useCallback(async (offset, append) => {
        append ? setLoadingMore(true) : setLoading(true);
        setError(null);
        try {
            const page = await userService.getActivityTimeline(id, { offset, limit: PAGE_SIZE });
            setUser(page.user ? { userId: page.user.userId, firstName: page.user.firstName, lastName: page.user.lastName, email: page.user.email } : null);
            setItems(prev => append ? [...prev, ...(page.data || [])] : (page.data || []));
            setTotalRecords(page.totalRecords || 0);
        } catch (e) {
            setError('Não foi possível carregar o histórico de atividades do usuário.');
        } finally {
            append ? setLoadingMore(false) : setLoading(false);
        }
    }, [id]);

    // Um único botão de reload no header pros dois painéis do accordion —
    // dispara timeline e heatmap juntos (Promise.all) em vez de precisar de
    // um botão de reload por painel.
    const handleReload = useCallback(() => {
        setReloading(true);
        return Promise.all([
            fetchPage(0, false),
            heatmapRef.current?.reload(),
        ]).finally(() => setReloading(false));
    }, [fetchPage]);

    useEffect(() => {
        setItems([]);
        setTotalRecords(0);
        setUser(location.state?.user ?? null);
        userService.getUser(id).then(fullUser => setUser(fullUser)).catch(() => {});
        fetchPage(0, false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    // Com PAGE_SIZE=10, a primeira leva pode não preencher a altura da tela —
    // nesse caso ainda NÃO existe scroll possível, então nenhum evento de
    // scroll dispararia o carregamento seguinte. Esse efeito garante que a
    // página cresça até ficar realmente rolável (ou até acabarem os
    // registros) — a partir daí, o carregamento das próximas páginas passa a
    // depender só do scroll de verdade do usuário (efeito abaixo).
    useEffect(() => {
        if (loading || loadingMore) return;
        if (items.length === 0 || items.length >= totalRecords) return;
        if (document.documentElement.scrollHeight <= window.innerHeight) {
            fetchPage(items.length, true);
        }
    }, [items, totalRecords, loading, loadingMore, fetchPage]);

    // Infinite scroll de verdade: acompanha o scroll da PÁGINA (não um box
    // interno com altura fixa) — ao chegar perto do fim do documento, busca a
    // próxima página e concatena ao que já foi carregado.
    useEffect(() => {
        const handleWindowScroll = () => {
            if (loading || loadingMore) return;
            if (items.length >= totalRecords) return;
            const scrollTop = window.scrollY || document.documentElement.scrollTop;
            const distanceToBottom = document.documentElement.scrollHeight - (scrollTop + window.innerHeight);
            if (distanceToBottom < SCROLL_THRESHOLD_PX) {
                fetchPage(items.length, true);
            }
        };
        window.addEventListener('scroll', handleWindowScroll);
        return () => window.removeEventListener('scroll', handleWindowScroll);
    }, [loading, loadingMore, items.length, totalRecords, fetchPage]);

    const timelineMarker = (item) => {
        if (item.kind === 'login_attempt') {
            return (
                <span
                    className="flex w-2rem h-2rem align-items-center justify-content-center text-white border-circle z-1 shadow-2"
                    style={{ backgroundColor: item.success ? '#22C55E' : '#EF4444' }}
                >
                    <i className={ATTEMPT_TYPE_ICON[item.attemptType] || 'pi pi-sign-in'} />
                </span>
            );
        }
        return (
            <span
                className="flex w-2rem h-2rem align-items-center justify-content-center text-white border-circle z-1 shadow-2"
                style={{ backgroundColor: '#607D8B' }}
            >
                <i className={platformIcon(item.platform)} />
            </span>
        );
    };

    // Estilo compartilhado pelas duas variantes de card — p-card usa fundo
    // branco fixo (nem var(--surface-card)) — no tema light isso é idêntico
    // ao fundo da própria página, então o card não se destaca. Mesmo par
    // border/background já usado em StudentTimelinePage.js pra esse problema.
    const cardStyle = { border: '1px solid var(--surface-300)', background: 'var(--surface-50)' };

    const loginAttemptContent = (item) => (
        <Card
            title={ATTEMPT_TYPE_LABEL[item.attemptType] || item.attemptType}
            className="mb-0"
            style={cardStyle}
        >
            <div className="flex justify-content-between align-items-center gap-2 mb-2">
                <span className="text-sm text-color-secondary">{formatDateTime(item.occurredAt)}</span>
                <Tag value={item.success ? 'Sucesso' : 'Falha'} severity={item.success ? 'success' : 'danger'} />
            </div>
            {!item.success && (
                <p className="m-0 text-sm">{ATTEMPT_REASON_LABEL[item.reason] || item.reason}</p>
            )}
            {item.ip && (
                <p className="m-0 mt-1 text-sm text-color-secondary">IP: {item.ip}</p>
            )}
            {item.deviceId && (
                <p className="m-0 mt-1 text-sm text-color-secondary">Dispositivo: {item.deviceId}</p>
            )}
        </Card>
    );

    const sessionContent = (item) => {
        const liveSeconds = item.endedAt
            ? item.durationSeconds
            : Math.max(0, Math.floor((now - new Date(item.occurredAt).getTime()) / 1000));

        return (
            <Card
                title={item.endedAt ? 'Sessão' : 'Sessão em andamento'}
                subTitle={[item.deviceModel, item.osVersion].filter(Boolean).join(' — ') || item.platform || null}
                className="mb-0"
                style={cardStyle}
            >
                <p className="m-0 mb-2 text-sm text-color-secondary">
                    {formatDateTime(item.occurredAt)} — {item.endedAt ? formatDateTime(item.endedAt) : 'em andamento'}
                </p>
                <p className="m-0 text-sm">
                    Duração: {formatDuration(liveSeconds)}
                    {' '}— {item.accessCount} acesso{item.accessCount === 1 ? '' : 's'}
                </p>
                {item.appVersion && (
                    <p className="m-0 mt-1 text-sm text-color-secondary">Versão do app: {item.appVersion}</p>
                )}
                {item.deviceId && (
                    <p className="m-0 mt-1 text-sm text-color-secondary">Dispositivo: {item.deviceId}</p>
                )}
                {item.ip && (
                    <p className="m-0 mt-1 text-sm text-color-secondary">IP: {item.ip}</p>
                )}
            </Card>
        );
    };

    const timelineContent = (item) => item.kind === 'login_attempt'
        ? loginAttemptContent(item)
        : sessionContent(item);

    return (
        <div className="grid p-fluid timeline-demo">
            <div className="col-12">
                <div className="card">
                    <div className="flex align-items-center justify-content-between mb-4">
                        <div className="flex align-items-center gap-3">
                            <Button
                                icon="pi pi-arrow-left"
                                rounded
                                text
                                // goBack() (POP) em vez de push('/users') — é o que
                                // deixa UsersView.js (forceRefreshRef, baseado em
                                // history.action) saber que isso foi um "voltar" e
                                // não uma navegação nova, pra não resetar a grid
                                // (mesmo padrão de UserDetailsPage.js).
                                onClick={() => history.goBack()}
                            />
                            <div>
                                <h5 className="m-0">{userDisplayName(user)}</h5>
                                {user?.email && (
                                    <span className="text-color-secondary text-sm">{user.email}</span>
                                )}
                            </div>
                        </div>
                        <Button
                            icon="pi pi-refresh"
                            rounded
                            text
                            onClick={handleReload}
                            disabled={reloading}
                        />
                    </div>

                    <Accordion multiple activeIndex={activeIndices} onTabChange={(e) => setActiveIndices(e.index)}>
                        <AccordionTab header="Heatmap de Sessões">
                            <SessionsCalendarHeatmap ref={heatmapRef} userId={id} />
                        </AccordionTab>
                        <AccordionTab header="Linha do Tempo">
                            {loading ? (
                                <div className="flex justify-content-center p-5">
                                    <ProgressSpinner />
                                </div>
                            ) : error ? (
                                <p>{error}</p>
                            ) : items.length === 0 ? (
                                <p>Nenhuma atividade registrada.</p>
                            ) : (
                                <>
                                    <Timeline
                                        value={items}
                                        align="alternate"
                                        className="customized-timeline"
                                        marker={timelineMarker}
                                        content={timelineContent}
                                    />
                                    {loadingMore && (
                                        <div className="flex justify-content-center p-3">
                                            <ProgressSpinner style={{ width: '2rem', height: '2rem' }} strokeWidth="4" />
                                        </div>
                                    )}
                                </>
                            )}
                        </AccordionTab>
                    </Accordion>
                </div>
            </div>
        </div>
    );
};

export default UserActivityTimelinePage;
